import * as Popover from '@radix-ui/react-popover'
import { BubbleMenu, type Editor } from '@tiptap/react'
import {
	Underline,
	Bold,
	Strikethrough,
	Italic,
	AlignLeft,
	AlignRight,
	AlignCenter,
	AlignJustify,
	Highlighter,
	Palette,
	Zap,
} from 'lucide-react'
import { memo } from 'react'
import { ColorPicker } from '../../panels'
import { Surface } from '../../ui/Surface'
import { Toolbar } from '../../ui/Toolbar'
// import { ContentTypePicker } from './components/ContentTypePicker'
import { EditLinkPopover } from './components/EditLinkPopover'
import { useTextmenuCommands } from './hooks/useTextmenuCommands'
// import { useTextmenuContentTypes } from './hooks/useTextmenuContentTypes'
import { useTextmenuStates } from './hooks/useTextmenuStates'

// We memorize the button so each button is not rerendered
// on every editor state change
const MemoButton = memo(Toolbar.Button)
const MemoColorPicker = memo(ColorPicker)
// const MemoContentTypePicker = memo(ContentTypePicker)

export type TextMenuProps = {
	editor: Editor
}

export const TextMenu = ({ editor }: TextMenuProps) => {
	const commands = useTextmenuCommands(editor)
	const states = useTextmenuStates(editor)
	// const blockOptions = useTextmenuContentTypes(editor)

	return (
		<BubbleMenu
			tippyOptions={{ popperOptions: { placement: 'top-start' } }}
			editor={editor}
			pluginKey="textMenu"
			shouldShow={states.shouldShow}
			updateDelay={100}
		>
			<div className="flex w-fit min-w-0 items-center rounded-xl border border-border bg-white p-1 shadow-sm">
				{/* <MemoContentTypePicker options={blockOptions} /> */}

				<MemoButton onClick={() => editor.chain().focus().setVariable().run()}>
					<Zap width={16} />
				</MemoButton>

				<Toolbar.Divider />

				<EditLinkPopover onSetLink={commands.onLink} />
				
				<Toolbar.Divider />

				<MemoButton
					tooltip="Bold"
					tooltipShortcut={['Mod', 'B']}
					onClick={commands.onBold}
					active={states.isBold}
				>
					<Bold width={16} />
				</MemoButton>
				<MemoButton
					tooltip="Italic"
					tooltipShortcut={['Mod', 'I']}
					onClick={commands.onItalic}
					active={states.isItalic}
				>
					<Italic width={16} />
				</MemoButton>
				<MemoButton
					tooltip="Underline"
					tooltipShortcut={['Mod', 'U']}
					onClick={commands.onUnderline}
					active={states.isUnderline}
				>
					<Underline width={16} />
				</MemoButton>
				<MemoButton
					tooltip="StrikeThrough"
					tooltipShortcut={['Mod', 'Shift', 'S']}
					onClick={commands.onStrike}
					active={states.isStrike}
				>
					<Strikethrough width={16} />
				</MemoButton>
				{/* <MemoButton
					tooltip="Code"
					tooltipShortcut={['Mod', 'E']}
					onClick={commands.onCode}
					active={states.isCode}
				>
					<Code width={16} />
				</MemoButton> */}

				<Toolbar.Divider />

				<MemoButton
					tooltip="Align left"
					tooltipShortcut={['Shift', 'Mod', 'L']}
					onClick={commands.onAlignLeft}
					active={states.isAlignLeft}
				>
					<AlignLeft width={16} />
				</MemoButton>
				<MemoButton
					tooltip="Align center"
					tooltipShortcut={['Shift', 'Mod', 'E']}
					onClick={commands.onAlignCenter}
					active={states.isAlignCenter}
				>
					<AlignCenter width={16} />
				</MemoButton>
				<MemoButton
					tooltip="Align right"
					tooltipShortcut={['Shift', 'Mod', 'R']}
					onClick={commands.onAlignRight}
					active={states.isAlignRight}
				>
					<AlignRight width={16} />
				</MemoButton>
				<MemoButton
					tooltip="Justify"
					tooltipShortcut={['Shift', 'Mod', 'J']}
					onClick={commands.onAlignJustify}
					active={states.isAlignJustify}
				>
					<AlignJustify width={16} />
				</MemoButton>

				<Toolbar.Divider />

				<Popover.Root>
					<Popover.Trigger asChild>
						<MemoButton active={!!states.currentColor} tooltip="Text color">
							<Palette width={16} />
						</MemoButton>
					</Popover.Trigger>
					<Popover.Content
						asChild
						sideOffset={12}
						side="top"
						align="end"
						alignOffset={0}
						className="custom-popover z-50"
					>
						<Surface>
							<MemoColorPicker
								color={states.currentColor}
								onChange={commands.onChangeColor}
								onClear={commands.onClearColor}
							/>
						</Surface>
					</Popover.Content>
				</Popover.Root>

				<Popover.Root>
					<Popover.Trigger asChild>
						<MemoButton
							active={!!states.currentHighlight}
							tooltip="Highlight text"
						>
							<Highlighter width={16} />
						</MemoButton>
					</Popover.Trigger>
					<Popover.Content
						sideOffset={12}
						side="top"
						align="end"
						alignOffset={-5}
						asChild
						className="custom-popover z-50 rounded-xl border border-border bg-white p-2 shadow-sm"
					>
						<Surface className="p-1">
							<MemoColorPicker
								color={states.currentHighlight}
								onChange={commands.onChangeHighlight}
								onClear={commands.onClearHighlight}
							/>
						</Surface>
					</Popover.Content>
				</Popover.Root>
			</div>
		</BubbleMenu>
	)
}
